import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import app from 'flarum/forum/app';
import Model from 'flarum/common/Model';

/**
 * Agregado no servidor (MessageResource::reactionSummary()) para o cliente não
 * receber cada reação.
 */

export default class Message extends Model {
  constructor() {
    super(...arguments);
    // ── Conteúdo ──
    /** Texto-fonte. Null em mensagem apagada que o usuário não pode ver. */
    _defineProperty(this, "content", Model.attribute('content'));
    _defineProperty(this, "contentHtml", Model.attribute('contentHtml'));
    _defineProperty(this, "type", Model.attribute('type'));
    _defineProperty(this, "systemKey", Model.attribute('systemKey'));
    _defineProperty(this, "systemData", Model.attribute('systemData'));
    // ── Posição ──
    _defineProperty(this, "number", Model.attribute('number'));
    _defineProperty(this, "channelId", Model.attribute('channelId'));
    _defineProperty(this, "threadId", Model.attribute('threadId'));
    _defineProperty(this, "replyToId", Model.attribute('replyToId'));
    _defineProperty(this, "createdAt", Model.attribute('createdAt', Model.transformDate));
    _defineProperty(this, "editedAt", Model.attribute('editedAt', Model.transformDate));
    _defineProperty(this, "deletedAt", Model.attribute('deletedAt', Model.transformDate));
    /**
     * Avança a cada mudança (edição, exclusão, fixar, reação). O polling de
     * fallback se reconcilia por ele (pollChanges() em forum/index.tsx).
     */
    _defineProperty(this, "updatedAt", Model.attribute('updatedAt', Model.transformDate));
    _defineProperty(this, "isDeleted", Model.attribute('isDeleted'));
    /**
     * Apagada por alguém que não o autor. `isRedacted()` só diz que o texto foi
     * ocultado deste leitor, o que vale também para autoexclusão.
     */
    _defineProperty(this, "isModeratorDeleted", Model.attribute('isModeratorDeleted'));
    _defineProperty(this, "isEdited", Model.attribute('isEdited'));
    _defineProperty(this, "isPinned", Model.attribute('isPinned'));
    _defineProperty(this, "pinnedAt", Model.attribute('pinnedAt', Model.transformDate));
    // ── Interação ──
    _defineProperty(this, "reactionSummary", Model.attribute('reactionSummary'));
    _defineProperty(this, "mentionedUsers", Model.attribute('mentionedUsers'));
    _defineProperty(this, "mentionsChannelWide", Model.attribute('mentionsChannelWide'));
    _defineProperty(this, "isBookmarked", Model.attribute('isBookmarked'));
    /**
     * Rank do autor no momento da leitura. O `rankBook` do canal, mantido em dia
     * pelo realtime, tem precedência quando carregado.
     */
    _defineProperty(this, "authorRank", Model.attribute('authorRank'));
    /** Se *este* leitor tem denúncia aberta contra ela. */
    _defineProperty(this, "isFlagged", Model.attribute('isFlagged'));
    /**
     * Só para quem tem `ramon-chat.moderate`; para os demais é undefined, não
     * zero, para não revelar quais mensagens foram denunciadas.
     */
    _defineProperty(this, "flagsCount", Model.attribute('flagsCount'));
    // ── Permissões ──
    _defineProperty(this, "canEdit", Model.attribute('canEdit'));
    _defineProperty(this, "canDelete", Model.attribute('canDelete'));
    _defineProperty(this, "canReact", Model.attribute('canReact'));
    _defineProperty(this, "canReply", Model.attribute('canReply'));
    _defineProperty(this, "canCreateThread", Model.attribute('canCreateThread'));
    _defineProperty(this, "canMove", Model.attribute('canMove'));
    _defineProperty(this, "canPin", Model.attribute('canPin'));
    _defineProperty(this, "canFlag", Model.attribute('canFlag'));
    /** Remover a linha de vez, sem deixar tombstone. */
    _defineProperty(this, "canForceDelete", Model.attribute('canForceDelete'));
    // ── Relações ──
    _defineProperty(this, "user", Model.hasOne('user'));
    _defineProperty(this, "editedBy", Model.hasOne('editedBy'));
    _defineProperty(this, "deletedBy", Model.hasOne('deletedBy'));
    _defineProperty(this, "replyTo", Model.hasOne('replyTo'));
    _defineProperty(this, "thread", Model.hasOne('thread'));
    _defineProperty(this, "channel", Model.hasOne('channel'));
    _defineProperty(this, "uploads", Model.hasMany('uploads'));
  }
  // ── Derivados ──

  isSystem() {
    return this.type() === 'system';
  }

  /**
   * Não é mensagem de sistema: renderiza como mensagem comum, mas sem registro
   * de usuário; nome e avatar vêm das settings.
   */
  isBot() {
    return this.type() === 'bot';
  }

  /**
   * A linha existe mas o texto foi ocultado deste leitor. O stream desenha um
   * tombstone para não haver reflow silencioso.
   */
  isRedacted() {
    return Boolean(this.isDeleted()) && this.content() === null;
  }

  /** Por nome ou via @here / @all. */
  mentionsActor() {
    var _this$mentionedUsers;
    const actor = app.session.user;
    if (!actor) return false;
    if (this.mentionsChannelWide()) return true;
    return ((_this$mentionedUsers = this.mentionedUsers()) != null ? _this$mentionedUsers : []).includes(Number(actor.id()));
  }

  /** Mesmo autor, perto no tempo e nenhuma das duas é de sistema. */
  isGroupedWith(previous, withinSeconds) {
    if (withinSeconds === void 0) {
      withinSeconds = 300;
    }
    if (!previous) return false;
    if (this.isSystem() || previous.isSystem()) return false;
    if (this.threadId() !== previous.threadId()) return false;

    // Posts do bot não têm `user()`; dois seguidos contam como mesmo autor, e
    // bot com humano nunca.
    if (this.isBot() || previous.isBot()) {
      if (!this.isBot() || !previous.isBot()) return false;
    } else {
      const a = this.user();
      const b = previous.user();
      if (!a || !b || a.id() !== b.id()) return false;
    }

    // A janela de tempo vale também para o bot.
    const t1 = this.createdAt();
    const t2 = previous.createdAt();
    if (!t1 || !t2) return false;
    return (t1.getTime() - t2.getTime()) / 1000 <= withinSeconds;
  }
  totalReactions() {
    var _this$reactionSummary;
    const summary = (_this$reactionSummary = this.reactionSummary()) != null ? _this$reactionSummary : {};
    return Object.values(summary).reduce((sum, entry) => sum + entry.count, 0);
  }
  apiEndpoint() {
    return '/chat-messages' + (this.exists ? '/' + this.id() : '');
  }
}
flarum.reg.add('ramon-chat', 'common/models/Message', Message);